import React, { useEffect, useState } from "react";
import { Avatar, Button, Checkbox, Group, Modal, ScrollArea, Stack, Text } from "@mantine/core";
import { getUserImageSrc } from "../../helpers/uploadImage";

export const groupCallMembers = (chat, currentUserId) =>
	Array.from(new Map(
		[...(chat?.users || []), ...(chat?.admins || [])]
			.filter((member) => member && typeof member === "object" && member._id)
			.filter((member) => String(member._id) !== String(currentUserId))
			.map((member) => [String(member._id), member])
	).values());

export default function GroupCallMemberPicker({ opened, onClose, members = [], excludedIds = [], onConfirm, title, actionLabel = "Invite" }) {
	const [selected, setSelected] = useState([]);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState("");
	const excluded = new Set(excludedIds.map(String));
	const available = members.filter((member) => !excluded.has(String(member._id)));

	useEffect(() => {
		if (opened) { setSelected([]); setError(""); }
	}, [opened]);

	const confirm = async () => {
		setBusy(true);
		setError("");
		try {
			const accepted = await onConfirm(selected);
			if (accepted === false) setError("Could not invite the selected members. Please try again.");
			else onClose();
		} catch (failure) {
			setError(failure?.message || "Could not invite the selected members. Please try again.");
		} finally {
			setBusy(false);
		}
	};

	return (
		<Modal opened={opened} onClose={onClose} title={title} centered size="sm" zIndex={9500}>
			<Stack spacing="sm">
				<Text size="sm" color="dimmed">Select group members to invite</Text>
				<ScrollArea style={{ maxHeight: "50vh" }}>
					<Stack spacing={4}>
						{available.map((member) => {
							const memberId = String(member._id);
							const name = typeof member.name === "object"
								? member.name.en || member.name.hy || member.name.ru || member.username
								: member.name || member.username || memberId;
							return (
							<Checkbox key={memberId} checked={selected.includes(memberId)}
								onChange={(event) => setSelected((current) => event.currentTarget.checked
									? [...current, memberId] : current.filter((id) => id !== memberId))}
								label={<Group spacing="xs" noWrap><Avatar size="sm" radius="xl" src={getUserImageSrc(member, { targetWidth: 40 })} /><Text size="sm">{name}</Text></Group>}
								styles={{ label: { width: "100%" }, root: { padding: "8px 4px" } }} />
						);
						})}
						{available.length === 0 && <Text size="sm" color="dimmed">No members available</Text>}
					</Stack>
				</ScrollArea>
				{error && <Text size="sm" color="red" role="alert">{error}</Text>}
				<Group position="right"><Button variant="subtle" onClick={onClose}>Cancel</Button>
					<Button disabled={selected.length === 0} loading={busy} onClick={confirm}>{actionLabel} ({selected.length})</Button></Group>
			</Stack>
		</Modal>
	);
}
